import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-forest-green via-midnight-green to-deep-teal">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <Button asChild variant="copper" className="mb-6">
          <Link to="/" className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </Button>

        <Card className="bg-sand/95 backdrop-blur-sm border-deep-teal/20">
          <ScrollArea className="h-[calc(100vh-12rem)] px-8 py-6">
            <div className="prose prose-slate max-w-none">
              <h1 className="text-4xl font-bold text-midnight-green mb-2">Privacy Policy</h1>
              <p className="text-sm text-forest-green/70 mb-8">
                <strong>Effective Date:</strong> January 1, 2025<br />
                <strong>Last Updated:</strong> January 1, 2025
              </p>

              {/* Table of Contents */}
              <Card className="bg-cream/50 p-6 mb-8 border-copper/20">
                <h2 className="text-xl font-semibold text-midnight-green mt-0 mb-4">Table of Contents</h2>
                <nav className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <a href="#introduction" className="text-deep-teal hover:text-copper transition-colors">1. Introduction</a>
                  <a href="#information-collected" className="text-deep-teal hover:text-copper transition-colors">2. Information We Collect</a>
                  <a href="#how-we-use" className="text-deep-teal hover:text-copper transition-colors">3. How We Use Your Information</a>
                  <a href="#how-we-share" className="text-deep-teal hover:text-copper transition-colors">4. How We Share Your Information</a>
                  <a href="#your-rights" className="text-deep-teal hover:text-copper transition-colors">5. Your Rights and Choices</a>
                  <a href="#data-security" className="text-deep-teal hover:text-copper transition-colors">6. Data Security</a>
                  <a href="#data-retention" className="text-deep-teal hover:text-copper transition-colors">7. Data Retention</a>
                  <a href="#childrens-privacy" className="text-deep-teal hover:text-copper transition-colors">8. Children's Privacy</a>
                  <a href="#international-transfers" className="text-deep-teal hover:text-copper transition-colors">9. International Data Transfers</a>
                  <a href="#third-party-links" className="text-deep-teal hover:text-copper transition-colors">10. Third-Party Links</a>
                  <a href="#california-shine" className="text-deep-teal hover:text-copper transition-colors">11. California "Shine the Light" Law</a>
                  <a href="#do-not-track" className="text-deep-teal hover:text-copper transition-colors">12. Do Not Track Signals</a>
                  <a href="#updates" className="text-deep-teal hover:text-copper transition-colors">13. Updates to This Privacy Policy</a>
                  <a href="#contact" className="text-deep-teal hover:text-copper transition-colors">14. Contact Information</a>
                  <a href="#california-disclosures" className="text-deep-teal hover:text-copper transition-colors">15. Additional California Disclosures</a>
                </nav>
              </Card>

              <section id="introduction" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">1. Introduction</h2>
                <p className="text-forest-green">
                  PA Advisor, LLC ("Company," "we," "us," or "our") operates CampChef Pro (the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
                </p>
                <p className="text-forest-green">
                  Please read this Privacy Policy carefully. By using the Service, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not agree with the terms of this Privacy Policy, please do not access the Service.
                </p>
              </section>

              <section id="information-collected" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">2. Information We Collect</h2>
                
                <h3 className="text-xl font-semibold text-deep-teal">2.1 Personal Information</h3>
                <p className="text-forest-green">
                  We collect information that identifies, relates to, describes, references, is reasonably capable of being associated with, or could reasonably be linked, directly or indirectly, with a particular consumer, household, or device ("Personal Information").
                </p>
                
                <h4 className="text-lg font-semibold text-midnight-green/80">Information You Provide Directly:</h4>
                <ul className="text-forest-green">
                  <li>Name and contact information (first name, last name, email address, phone number)</li>
                  <li>Organization information (organization name, role, job title)</li>
                  <li>Account credentials (username, password)</li>
                  <li>Billing information (processed securely through Stripe - we do not store credit card numbers)</li>
                  <li>Communications (support requests, feedback, correspondence)</li>
                </ul>

                <h4 className="text-lg font-semibold text-midnight-green/80">Information Collected Automatically:</h4>
                <ul className="text-forest-green">
                  <li>Usage data (features used, frequency of use, interaction patterns)</li>
                  <li>Device information (IP address, browser type, operating system)</li>
                  <li>Cookies and similar tracking technologies</li>
                  <li>Log data (access times, pages viewed, app crashes, system activity)</li>
                </ul>

                <h4 className="text-lg font-semibold text-midnight-green/80">Information Related to Your Organization's Use:</h4>
                <ul className="text-forest-green">
                  <li>Recipe data and meal planning information</li>
                  <li>Inventory and ordering data</li>
                  <li>Menu configurations</li>
                  <li>User activity within your organization</li>
                </ul>

                <h3 className="text-xl font-semibold text-deep-teal">2.2 California Consumer Privacy Act (CCPA) Disclosure</h3>
                <p className="text-forest-green">
                  Under California law, we are required to provide California residents with additional information about the categories of Personal Information we collect and share. This information is included throughout this Privacy Policy.
                </p>
              </section>

              <section id="how-we-use" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">3. How We Use Your Information</h2>
                <p className="text-forest-green">We use the information we collect for the following purposes:</p>
                <ul className="text-forest-green">
                  <li><strong>Service Provision:</strong> To create and manage your account, provide meal planning functionality, and deliver the services you request</li>
                  <li><strong>Communication:</strong> To send service-related notices, respond to inquiries, and provide customer support</li>
                  <li><strong>Improvement:</strong> To understand how users interact with our Service and improve functionality</li>
                  <li><strong>Security:</strong> To detect, prevent, and address technical issues and protect against fraudulent or illegal activity</li>
                  <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
                  <li><strong>Marketing:</strong> With your consent, to send promotional communications about features, updates, and related services</li>
                </ul>
              </section>

              <section id="how-we-share" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">4. How We Share Your Information</h2>
                <p className="text-forest-green">
                  We do not sell, trade, or rent your Personal Information to third parties. We may share your information in the following situations:
                </p>

                <h3 className="text-xl font-semibold text-deep-teal">4.1 Service Providers</h3>
                <p className="text-forest-green">We may share your information with third-party service providers who perform services on our behalf:</p>
                <ul className="text-forest-green">
                  <li><strong>Supabase</strong> (database and authentication services)</li>
                  <li><strong>Stripe</strong> (payment processing)</li>
                  <li><strong>Resend</strong> (email communications)</li>
                  <li><strong>Cloud infrastructure providers</strong> (hosting and storage)</li>
                </ul>

                <h3 className="text-xl font-semibold text-deep-teal">4.2 Within Your Organization</h3>
                <p className="text-forest-green">
                  Information about your account and activities may be visible to other authorized users within your organization, including administrators.
                </p>

                <h3 className="text-xl font-semibold text-deep-teal">4.3 Legal Requirements</h3>
                <p className="text-forest-green">
                  We may disclose your information if required to do so by law or in response to valid requests by public authorities.
                </p>

                <h3 className="text-xl font-semibold text-deep-teal">4.4 Business Transfers</h3>
                <p className="text-forest-green">
                  If we are involved in a merger, acquisition, or asset sale, your Personal Information may be transferred. We will provide notice before your Personal Information is transferred and becomes subject to a different Privacy Policy.
                </p>
              </section>

              <section id="your-rights" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">5. Your Rights and Choices</h2>
                
                <h3 className="text-xl font-semibold text-deep-teal">5.1 California Privacy Rights</h3>
                <p className="text-forest-green">California residents have the following rights under the CCPA:</p>
                <ul className="text-forest-green">
                  <li><strong>Right to Know:</strong> You have the right to request information about the categories and specific pieces of Personal Information we have collected about you</li>
                  <li><strong>Right to Delete:</strong> You have the right to request deletion of your Personal Information, subject to certain exceptions</li>
                  <li><strong>Right to Opt-Out:</strong> You have the right to opt-out of the sale of your Personal Information (Note: We do not sell Personal Information)</li>
                  <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights</li>
                  <li><strong>Right to Correct:</strong> You have the right to request correction of inaccurate Personal Information</li>
                </ul>
                <p className="text-forest-green">
                  To exercise these rights, contact us at: <a href="mailto:info@paadvisor.com" className="text-copper hover:underline">info@paadvisor.com</a>
                </p>

                <h3 className="text-xl font-semibold text-deep-teal">5.2 Additional Rights</h3>
                <ul className="text-forest-green">
                  <li><strong>Access and Portability:</strong> You can access and download your data through the Service</li>
                  <li><strong>Update Information:</strong> You can update your account information through your profile settings</li>
                  <li><strong>Marketing Communications:</strong> You can opt-out of marketing emails by clicking "unsubscribe" in any marketing email</li>
                  <li><strong>Cookies:</strong> You can control cookies through your browser settings</li>
                </ul>
              </section>

              <section id="data-security" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">6. Data Security</h2>
                <p className="text-forest-green">
                  We implement appropriate technical and organizational security measures to protect your Personal Information, including:
                </p>
                <ul className="text-forest-green">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Row-level security policies in our database</li>
                  <li>Regular security assessments and updates</li>
                  <li>Access controls and authentication requirements</li>
                  <li>Regular backups and disaster recovery procedures</li>
                </ul>
                <p className="text-forest-green">
                  However, no electronic transmission or storage of information can be guaranteed to be 100% secure. Please use caution when transmitting Personal Information to us.
                </p>
              </section>

              <section id="data-retention" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">7. Data Retention</h2>
                <p className="text-forest-green">
                  We retain your Personal Information for as long as necessary to provide the Service and fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it.
                </p>
              </section>

              <section id="childrens-privacy" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">8. Children's Privacy</h2>
                <p className="text-forest-green">
                  Our Service is not directed to individuals under the age of 16. We do not knowingly collect Personal Information from children under 16. If you become aware that a child has provided us with Personal Information, please contact us immediately.
                </p>
              </section>

              <section id="international-transfers" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">9. International Data Transfers</h2>
                <p className="text-forest-green">
                  Your information may be transferred to and maintained on servers located outside of your state, province, country, or other governmental jurisdiction where data protection laws may differ. Your consent to this Privacy Policy followed by your submission of such information represents your agreement to that transfer.
                </p>
              </section>

              <section id="third-party-links" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">10. Third-Party Links</h2>
                <p className="text-forest-green">
                  Our Service may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
                </p>
              </section>

              <section id="california-shine" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">11. California "Shine the Light" Law</h2>
                <p className="text-forest-green">
                  California Civil Code Section 1798.83 permits users who are California residents to request certain information regarding our disclosure of Personal Information to third parties for their direct marketing purposes. We do not share Personal Information with third parties for their direct marketing purposes.
                </p>
              </section>

              <section id="do-not-track" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">12. Do Not Track Signals</h2>
                <p className="text-forest-green">
                  We do not currently respond to Do Not Track browser signals or similar mechanisms.
                </p>
              </section>

              <section id="updates" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">13. Updates to This Privacy Policy</h2>
                <p className="text-forest-green">We may update this Privacy Policy from time to time. We will notify you of any changes by:</p>
                <ul className="text-forest-green">
                  <li>Posting the new Privacy Policy on this page</li>
                  <li>Updating the "Last Updated" date</li>
                  <li>Sending an email notification for material changes</li>
                </ul>
                <p className="text-forest-green">
                  You are advised to review this Privacy Policy periodically for any changes.
                </p>
              </section>

              <section id="contact" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">14. Contact Information</h2>
                <p className="text-forest-green">
                  If you have questions or concerns about this Privacy Policy or our privacy practices, please contact us:
                </p>
                <div className="bg-cream/50 p-4 rounded-lg border border-copper/20 my-4">
                  <p className="text-forest-green mb-2"><strong>PA Advisor, LLC</strong></p>
                  <p className="text-forest-green">Email: <a href="mailto:info@paadvisor.com" className="text-copper hover:underline">info@paadvisor.com</a></p>
                  <p className="text-forest-green">Phone: 760-576-8002</p>
                  <p className="text-forest-green">Address: 1389 Sky Ridge Ct. San Marcos, CA 92078</p>
                </div>
                <p className="text-forest-green">
                  <strong>Data Protection Officer:</strong><br />
                  Email: <a href="mailto:info@paadvisor.com" className="text-copper hover:underline">info@paadvisor.com</a>
                </p>
                <p className="text-forest-green mt-4">
                  For California residents, you may also contact the California Attorney General's office:
                </p>
                <ul className="text-forest-green">
                  <li>Online: <a href="https://oag.ca.gov/privacy" target="_blank" rel="noopener noreferrer" className="text-copper hover:underline">oag.ca.gov/privacy</a></li>
                  <li>Phone: (916) 445-9555</li>
                </ul>
              </section>

              <section id="california-disclosures" className="mb-8">
                <h2 className="text-2xl font-semibold text-midnight-green">15. Additional California Disclosures</h2>
                
                <h3 className="text-xl font-semibold text-deep-teal">Categories of Personal Information Collected (Last 12 Months):</h3>
                <ul className="text-forest-green">
                  <li>Identifiers (name, email, IP address)</li>
                  <li>Commercial information (purchase history, account information)</li>
                  <li>Internet activity (usage data, interactions with Service)</li>
                  <li>Professional information (employer, job title)</li>
                </ul>

                <h3 className="text-xl font-semibold text-deep-teal">Sources of Personal Information:</h3>
                <ul className="text-forest-green">
                  <li>Directly from you</li>
                  <li>Automatically from your use of the Service</li>
                  <li>From your organization's administrator</li>
                </ul>

                <h3 className="text-xl font-semibold text-deep-teal">Business Purposes for Collection:</h3>
                <ul className="text-forest-green">
                  <li>Providing and maintaining the Service</li>
                  <li>Processing transactions</li>
                  <li>Detecting security incidents</li>
                  <li>Debugging and error repair</li>
                  <li>Legal compliance</li>
                  <li>Internal analytics</li>
                </ul>
              </section>

              <div className="bg-copper/10 p-6 rounded-lg border-2 border-copper/30 mt-8">
                <p className="text-midnight-green font-semibold text-center">
                  BY USING CAMPCHEF PRO, YOU ACKNOWLEDGE THAT YOU HAVE READ AND UNDERSTOOD THIS PRIVACY POLICY AND AGREE TO ITS TERMS.
                </p>
              </div>
            </div>
          </ScrollArea>
        </Card>
      </div>
    </div>
  );
}
